import type { AppConfig, FramePayload, Manifest, ManifestFrame, PolygonView } from '../shared/types';

/**
 * Central app state. Modules mutate it and emit coarse events:
 *   'frame'     — the displayed snapshot changed
 *   'payload'   — polygons/connections for the current frame were (re)loaded
 *   'selection' — the selected polygon changed
 *   'admin'     — admin session started or ended
 *   'annotated' — the set of annotated frames changed
 */
class AppState extends EventTarget {
  config: AppConfig = { sitekey: '' };
  manifest: Manifest = { fps: 24000 / 1001, duration: 0, video: [], frames: [] };
  admin = false;
  frameIndex = -1;
  payload: FramePayload | null = null;
  selectedPolygonId: number | null = null;
  /** frame_ms -> polygon count */
  annotated = new Map<number, number>();

  get currentFrame(): ManifestFrame | null {
    return this.frameIndex >= 0 ? this.manifest.frames[this.frameIndex] ?? null : null;
  }

  get selectedPolygon(): PolygonView | null {
    if (this.selectedPolygonId === null || !this.payload) return null;
    return this.payload.polygons.find((p) => p.id === this.selectedPolygonId) ?? null;
  }

  selectPolygon(id: number | null): void {
    if (this.selectedPolygonId === id) return;
    this.selectedPolygonId = id;
    this.emit('selection');
  }

  emit(type: 'frame' | 'payload' | 'selection' | 'admin' | 'annotated'): void {
    this.dispatchEvent(new Event(type));
  }
}

export const state = new AppState();
